import React, { useEffect, useMemo, useState } from 'react';
import { Star, Plus, Pencil, Trash2, X, Save, Package } from 'lucide-react';
import {
  fetchBestProductsSection,
  updateBestProductsSection,
  fetchBestProductTabs,
  createBestProductTab,
  updateBestProductTab,
  deleteBestProductTab,
  setBestProductTabProducts,
} from '../api/bestProductsApi';
import { fetchProducts, fetchCategories, getProductImageUrl } from '../api/productsApi';

const EMPTY_SECTION = {
  label: 'Curated For You',
  title: 'Best Products',
  subtitle: '',
  ctaLabel: 'View All Products',
  ctaUrl: '/products',
  isActive: true,
};

const EMPTY_TAB = {
  name: '',
  slug: '',
  categoryId: '',
  sortOrder: 0,
  isActive: true,
};

const BestProductsAdmin = () => {
  const [section, setSection] = useState(EMPTY_SECTION);
  const [tabs, setTabs] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeTabId, setActiveTabId] = useState(null);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingSection, setSavingSection] = useState(false);
  const [savingProducts, setSavingProducts] = useState(false);
  const [error, setError] = useState('');
  const [tabModalOpen, setTabModalOpen] = useState(false);
  const [editingTab, setEditingTab] = useState(null);
  const [tabForm, setTabForm] = useState(EMPTY_TAB);
  const [savingTab, setSavingTab] = useState(false);
  const [productSearch, setProductSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [sec, tabList, prodList, catList] = await Promise.all([
        fetchBestProductsSection(),
        fetchBestProductTabs(),
        fetchProducts(),
        fetchCategories().catch(() => []),
      ]);
      setSection(sec || EMPTY_SECTION);
      setTabs(tabList);
      setProducts(prodList);
      setCategories(Array.isArray(catList) ? catList : []);
      if (!activeTabId && tabList.length) {
        setActiveTabId(tabList[0].id);
        setSelectedProductIds(tabList[0].productIds || []);
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load best products');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const tab = tabs.find((t) => t.id === activeTabId);
    if (tab) setSelectedProductIds(tab.productIds || []);
  }, [activeTabId, tabs]);

  const activeTab = useMemo(
    () => tabs.find((t) => t.id === activeTabId) || null,
    [tabs, activeTabId]
  );

  const filteredProducts = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) =>
      String(p.name || '').toLowerCase().includes(q) ||
      String(p.category || '').toLowerCase().includes(q)
    );
  }, [products, productSearch]);

  const handleSectionChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSection((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const saveSection = async (e) => {
    e.preventDefault();
    setSavingSection(true);
    setError('');
    try {
      await updateBestProductsSection(section);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to save section');
    } finally {
      setSavingSection(false);
    }
  };

  const openCreateTab = () => {
    setEditingTab(null);
    setTabForm({ ...EMPTY_TAB, sortOrder: tabs.length });
    setTabModalOpen(true);
  };

  const openEditTab = (tab) => {
    setEditingTab(tab);
    setTabForm({
      name: tab.name,
      slug: tab.slug,
      categoryId: tab.categoryId || '',
      sortOrder: tab.sortOrder,
      isActive: tab.isActive,
    });
    setTabModalOpen(true);
  };

  const handleTabFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setTabForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const saveTab = async (e) => {
    e.preventDefault();
    setSavingTab(true);
    setError('');
    try {
      const payload = {
        ...tabForm,
        categoryId: tabForm.categoryId ? Number(tabForm.categoryId) : null,
      };
      if (editingTab) {
        await updateBestProductTab(editingTab.id, payload);
      } else {
        await createBestProductTab(payload);
      }
      setTabModalOpen(false);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to save tab');
    } finally {
      setSavingTab(false);
    }
  };

  const removeTab = async (id) => {
    if (!window.confirm('Delete this tab and its product assignments?')) return;
    try {
      await deleteBestProductTab(id);
      if (activeTabId === id) setActiveTabId(null);
      await load();
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Delete failed');
    }
  };

  const toggleProduct = (productId) => {
    setSelectedProductIds((prev) =>
      prev.includes(productId) ? prev.filter((id) => id !== productId) : [...prev, productId]
    );
  };

  const saveTabProducts = async () => {
    if (!activeTabId) return;
    setSavingProducts(true);
    setError('');
    try {
      await setBestProductTabProducts(activeTabId, selectedProductIds);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to save products');
    } finally {
      setSavingProducts(false);
    }
  };

  if (loading) {
    return <p className="text-slate-500 p-6">Loading best products settings…</p>;
  }

  return (
    <div className="ui-page animate-fade-in">
      <div className="mb-8">
        <h1 className="text-3xl font-extrabold text-slate-800 tracking-tight text-gradient flex items-center gap-2">
          <Star size={28} className="text-emerald-600" /> Best Products
        </h1>
        <p className="text-slate-500 mt-1 font-medium text-sm">
          Manage the homepage Best Products section — header, tabs (Beauty, Health food, Honey), and products per tab.
        </p>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">{error}</div>
      )}

      <form onSubmit={saveSection} className="ui-card ui-card-pad mb-6 space-y-4">
        <h2 className="text-lg font-bold text-slate-800">Section header</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="ui-label">Label</label>
            <input name="label" value={section.label} onChange={handleSectionChange} className="ui-input w-full" />
          </div>
          <div>
            <label className="ui-label">Title</label>
            <input name="title" value={section.title} onChange={handleSectionChange} className="ui-input w-full" required />
          </div>
        </div>
        <div>
          <label className="ui-label">Subtitle</label>
          <textarea name="subtitle" value={section.subtitle} onChange={handleSectionChange} rows={2} className="ui-input w-full" />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="ui-label">Button label</label>
            <input name="ctaLabel" value={section.ctaLabel} onChange={handleSectionChange} className="ui-input w-full" />
          </div>
          <div>
            <label className="ui-label">Button URL</label>
            <input name="ctaUrl" value={section.ctaUrl} onChange={handleSectionChange} className="ui-input w-full" />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <input type="checkbox" name="isActive" checked={section.isActive} onChange={handleSectionChange} />
          Section visible on website
        </label>
        <button type="submit" disabled={savingSection} className="ui-btn ui-btn-primary inline-flex items-center gap-2">
          <Save size={16} /> {savingSection ? 'Saving…' : 'Save section'}
        </button>
      </form>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="ui-card ui-card-pad lg:col-span-1">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-slate-800">Tabs</h2>
            <button type="button" onClick={openCreateTab} className="ui-btn ui-btn-outline inline-flex items-center gap-1 text-sm">
              <Plus size={14} /> Add tab
            </button>
          </div>
          <div className="space-y-2">
            {tabs.map((tab) => (
              <div
                key={tab.id}
                className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer transition-colors ${
                  activeTabId === tab.id ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:bg-slate-50'
                }`}
                onClick={() => setActiveTabId(tab.id)}
              >
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-800 truncate">{tab.name}</p>
                  <p className="text-xs text-slate-500">{tab.productIds?.length || 0} products</p>
                </div>
                <button type="button" onClick={(e) => { e.stopPropagation(); openEditTab(tab); }} className="p-1.5 rounded hover:bg-white">
                  <Pencil size={14} />
                </button>
                <button type="button" onClick={(e) => { e.stopPropagation(); removeTab(tab.id); }} className="p-1.5 rounded hover:bg-white text-rose-600">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            {!tabs.length && <p className="text-sm text-slate-500">No tabs yet.</p>}
          </div>
        </div>

        <div className="ui-card ui-card-pad lg:col-span-2">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <div>
              <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <Package size={18} className="text-emerald-600" />
                Products for: {activeTab?.name || '—'}
              </h2>
              <p className="text-xs text-slate-500 mt-1">
                Pick products to show when this tab is selected. If none are picked, products from the linked category are used.
              </p>
            </div>
            <button
              type="button"
              onClick={saveTabProducts}
              disabled={!activeTabId || savingProducts}
              className="ui-btn ui-btn-primary inline-flex items-center gap-2"
            >
              <Save size={16} /> {savingProducts ? 'Saving…' : 'Save products'}
            </button>
          </div>

          <input
            type="search"
            placeholder="Search products…"
            value={productSearch}
            onChange={(e) => setProductSearch(e.target.value)}
            className="ui-input w-full mb-4"
          />

          <div className="grid sm:grid-cols-2 gap-3 max-h-[480px] overflow-y-auto pr-1">
            {filteredProducts.map((p) => {
              const checked = selectedProductIds.includes(p.id);
              const img = p.imageUrl || getProductImageUrl(p.images?.[0]?.image_path);
              return (
                <label
                  key={p.id}
                  className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                    checked ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  <input type="checkbox" checked={checked} onChange={() => toggleProduct(p.id)} />
                  <div className="w-12 h-12 rounded-lg bg-slate-100 overflow-hidden shrink-0">
                    {img ? <img src={img} alt="" className="w-full h-full object-cover" /> : null}
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-sm text-slate-800 truncate">{p.name}</p>
                    <p className="text-xs text-slate-500 truncate">{p.category || '—'}</p>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      </div>

      {tabModalOpen && (
        <div className="ui-modal-overlay">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-slate-100">
              <h3 className="text-lg font-bold text-slate-800">{editingTab ? 'Edit tab' : 'Add tab'}</h3>
              <button type="button" onClick={() => setTabModalOpen(false)} className="p-2 rounded-lg hover:bg-slate-100">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={saveTab} className="p-5 space-y-4">
              <div>
                <label className="ui-label">Tab name *</label>
                <input name="name" value={tabForm.name} onChange={handleTabFormChange} className="ui-input w-full" required placeholder="Beauty" />
              </div>
              <div>
                <label className="ui-label">Slug (optional)</label>
                <input name="slug" value={tabForm.slug} onChange={handleTabFormChange} className="ui-input w-full" placeholder="beauty" />
              </div>
              <div>
                <label className="ui-label">Fallback category (if no products picked)</label>
                <select name="categoryId" value={tabForm.categoryId} onChange={handleTabFormChange} className="ui-input w-full">
                  <option value="">None</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="ui-label">Sort order</label>
                <input type="number" name="sortOrder" min="0" value={tabForm.sortOrder} onChange={handleTabFormChange} className="ui-input w-full" />
              </div>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <input type="checkbox" name="isActive" checked={tabForm.isActive} onChange={handleTabFormChange} />
                Active
              </label>
              <div className="flex gap-3">
                <button type="submit" disabled={savingTab} className="ui-btn ui-btn-primary">
                  {savingTab ? 'Saving…' : editingTab ? 'Update' : 'Create'}
                </button>
                <button type="button" onClick={() => setTabModalOpen(false)} className="ui-btn ui-btn-outline">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BestProductsAdmin;
